// src/domains/managingJson/components/RecordingConfig/index.tsx

import { useEffect, useState } from "react"
import { observer } from "mobx-react"
import {
  Form,
  InputNumber,
  Select,
  Switch,
  Button,
  Collapse,
  Typography,
  Space,
  Spin,
  Card,
  Tooltip,
  Alert,
} from "antd"
import { ArrowDownOutlined, ArrowUpOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons"
import { useStore } from "stores/utils/use-store"
import { notifications } from "notifications"
import type { EncoderName, RecordingConfigType } from "domains/managingJson/store/types"
import { DEFAULT_RECORDING_CONFIG } from "./defaults"
import {
  CAPTURE_MOUSE_CURSOR_VALUES,
  CLAMPS,
  ENCODER_NAMES,
  POINTER_TRACKING_TYPES,
  RESOLUTION_MODE_VALUES,
  encoderFamily,
  presetsForFamily,
  rateControlsForFamily,
} from "./constants"

const { Title, Text, Paragraph } = Typography
const { Panel } = Collapse
const { Option } = Select

const RANGE_HINT = (min: number, max: number) => `Range: ${min}–${max}`

export const RecordingConfig = observer(() => {
  const { dataStore: { managingJsonStore } } = useStore()

  const [form] = Form.useForm<RecordingConfigType>()
  const [loading, setLoading] = useState(true)
  const [existing, setExisting] = useState<RecordingConfigType | undefined>()
  const resolutionMode = Form.useWatch(["video", "resolutionMode"], form)
  const isNative = resolutionMode === "native"
  const pointerTrackingType = Form.useWatch(["pointerTracking", "options", "type"], form)
  const isEfficientTracker = pointerTrackingType === "efficient"

  useEffect(() => {
    (async() => {
      await managingJsonStore.getData()
      const item = managingJsonStore.itemTypes?.find(
        i => i.type === "recordingConfig",
      )?.data as RecordingConfigType | undefined
      setExisting(item)
      // Deep-merge defaults to fill any missing fields on partial remote payloads.
      const merged = deepMerge(DEFAULT_RECORDING_CONFIG, item ?? {})
      form.setFieldsValue(merged)
      setLoading(false)
    })()
  }, [managingJsonStore, form])

  const onSave = async() => {
    try {
      const values = await form.validateFields()
      const payload = { name: "recordingConfig", type: "recordingConfig" as const, data: values }

      if (existing) {
        await managingJsonStore.update(payload)
      } else {
        await managingJsonStore.create(payload)
      }
      notifications.success("Recording config saved")
      await managingJsonStore.getData()
      setExisting(values)
    } catch (err) {
      console.error("Failed to save recording config:", err)
      notifications.error("Failed to save recording config")
    }
  }

  if (loading) return <Spin />

  return (
    <Form form={form} layout="vertical" style={{ maxWidth: 900 }}>
      <Title level={2}>Match Recording</Title>
      <Paragraph type="secondary">
        Controls per-match video recording on the Buff desktop client.
        Schema mirrors <code>recordingConfig</code> in the desktop spec; ranges are enforced client-side too.
      </Paragraph>

      <Card style={{ marginBottom: 16 }}>
        <Form.Item
          name="enabled"
          label={<Text strong>Recording enabled (global kill switch)</Text>}
          valuePropName="checked"
          extra={
            "When off, the desktop client no-ops every matchCapture.start() call. "
            + "The feature flag rollout still gates which users see the feature; this is the emergency global switch."
          }
        >
          <Switch />
        </Form.Item>
      </Card>

      <Collapse defaultActiveKey={["video", "encoders"]}>
        <Panel header="Video" key="video" forceRender>
          <Form.Item
            label="Resolution mode"
            name={["video", "resolutionMode"]}
            extra={
              "forced: always use the Width/Height below (current default). "
              + "native: use the game window's logical resolution (falls back to monitor). "
              + "Width/Height inputs are ignored in native mode."
            }
            rules={[{ required: true }]}
          >
            <Select>
              {RESOLUTION_MODE_VALUES.map(v => (
                <Option key={v} value={v}>{v}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            label="FPS"
            name={["video", "fps"]}
            extra={RANGE_HINT(CLAMPS.fps.min, CLAMPS.fps.max)}
            rules={[{ required: true }]}
          >
            <InputNumber min={CLAMPS.fps.min} max={CLAMPS.fps.max} />
          </Form.Item>
          <Form.Item
            label="Width"
            name={["video", "width"]}
            extra={isNative ? "Ignored in native mode." : RANGE_HINT(CLAMPS.width.min, CLAMPS.width.max)}
            rules={[{ required: true }]}
          >
            <InputNumber min={CLAMPS.width.min} max={CLAMPS.width.max} step={2} disabled={isNative} />
          </Form.Item>
          <Form.Item
            label="Height"
            name={["video", "height"]}
            extra={isNative ? "Ignored in native mode." : RANGE_HINT(CLAMPS.height.min, CLAMPS.height.max)}
            rules={[{ required: true }]}
          >
            <InputNumber min={CLAMPS.height.min} max={CLAMPS.height.max} step={2} disabled={isNative} />
          </Form.Item>
          <Form.Item
            label="Max bitrate (kbps)"
            name={["video", "maxKbps"]}
            extra={RANGE_HINT(CLAMPS.maxKbps.min, CLAMPS.maxKbps.max)}
            rules={[{ required: true }]}
          >
            <InputNumber min={CLAMPS.maxKbps.min} max={CLAMPS.maxKbps.max} step={500} />
          </Form.Item>
          <Form.Item
            label="Auto-calc bitrate"
            name={["video", "autoCalcKbps"]}
            valuePropName="checked"
            extra={
              "When ON, Overwolf ignores the Max bitrate above and picks its own value — "
              + "and may also downscale the captured resolution in native mode to fit its chosen bitrate "
              + "(e.g. 1440p → 720p). Leave OFF unless you specifically want OW to manage bitrate for you."
            }
          >
            <Switch />
          </Form.Item>
          <Form.Item label="Capture mouse cursor" name={["video", "captureMouseCursor"]} rules={[{ required: true }]}>
            <Select>
              {CAPTURE_MOUSE_CURSOR_VALUES.map(v => <Option key={v} value={v}>{v}</Option>)}
            </Select>
          </Form.Item>
        </Panel>

        <Panel header="Audio" key="audio" forceRender>
          <Form.Item label="Capture game audio" name={["audio", "gameEnabled"]} valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item label="Game audio volume" name={["audio", "gameVolume"]} extra={RANGE_HINT(0, 100)}>
            <InputNumber min={CLAMPS.volume.min} max={CLAMPS.volume.max} />
          </Form.Item>
          <Form.Item label="Capture microphone" name={["audio", "micEnabled"]} valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item label="Microphone volume" name={["audio", "micVolume"]} extra={RANGE_HINT(0, 100)}>
            <InputNumber min={CLAMPS.volume.min} max={CLAMPS.volume.max} />
          </Form.Item>
          <Form.Item
            label="Filtered capture"
            name={["audio", "filteredCaptureEnabled"]}
            valuePropName="checked"
            extra="When ON, Overwolf uses filtered audio capture (excludes VOIP apps like Discord). Default ON."
          >
            <Switch />
          </Form.Item>
        </Panel>

        <Panel header="Sync Marker (advanced)" key="markerOverlay" forceRender>
          <Alert
            message={
              "When enabled, the desktop client paints a small 16x16 white block in the bottom-right corner "
              + "of the video at two known moments per recording (start + end). "
              + "The cloud pipeline detects these markers to align video frames with session-log timestamps "
              + "with sub-frame precision. "
              + "Side-effect: players see a brief flash in the corner during gameplay. "
              + "Default OFF; enable for ML-data-collection cohorts only."
            }
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
          <Form.Item
            label="Marker enabled"
            name={["markerOverlay", "enabled"]}
            valuePropName="checked"
            extra="Off by default. Turning on requires capture_overwolf_windows=true on the desktop side (already configured)."
          >
            <Switch />
          </Form.Item>
          <Form.Item
            label="Marker duration (ms)"
            name={["markerOverlay", "durationMs"]}
            extra={
              "Currently informational — runtime pulse is a hardcoded 250ms 3-blink pattern. "
              + "Kept for forward-compat with future tuning. Clamp [10, 10000]."
            }
            rules={[{ required: true }]}
          >
            <InputNumber min={10} max={10000} step={50} />
          </Form.Item>
          <Form.Item
            label="Prewarm delay (ms)"
            name={["markerOverlay", "prewarmDelayMs"]}
            extra={
              "Wait after marker window READY before continuing — gives topmost + position calls "
              + "time to settle. Clamp [10, 30000]."
            }
            rules={[{ required: true }]}
          >
            <InputNumber min={10} max={30000} step={100} />
          </Form.Item>
          <Form.Item
            label="Post-start delay (ms)"
            name={["markerOverlay", "postStartDelayMs"]}
            extra={
              "Wait after streaming.start callback before firing the start pulse, to let the encoder warm up. "
              + "Encoder warmup can take 10s+ on some setups; if the start marker is missing from the MP4, raise this. "
              + "Clamp [10, 60000]."
            }
            rules={[{ required: true }]}
          >
            <InputNumber min={10} max={60000} step={100} />
          </Form.Item>
          <Form.Item
            label="Post-end delay (ms)"
            name={["markerOverlay", "postEndDelayMs"]}
            extra={
              "Wait after firing the end pulse before calling streaming.stop, so marker frames flush to the MP4. "
              + "If the end marker is missing or clipped, raise this. Clamp [10, 30000]."
            }
            rules={[{ required: true }]}
          >
            <InputNumber min={10} max={30000} step={100} />
          </Form.Item>
        </Panel>

        <Panel header="Pointer Tracking" key="pointerTracking" forceRender>
          <Alert
            message={
              "Captures raw mouse + gamepad input alongside the video and writes it as a sibling file next to "
              + "the MP4 (<basename>.pointers.bin or .pointers.ndjson). Two tracker variants: "
              + "'efficient' — compact binary stream with QPC timestamps and TIMESYNC anchors; the cloud "
              + "pipeline trims this against the video timeline. "
              + "'inefficient' — line-delimited JSON, polled at a fixed rate, easy to inspect by hand; "
              + "the cloud pipeline currently does NOT trim NDJSON output. "
              + "Default: inefficient, because it's easier to debug while we shake out the binary path."
            }
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
          <Form.Item
            label="Pointer tracking enabled"
            name={["pointerTracking", "enabled"]}
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
          <Form.Item
            label="Client-side processing"
            name={["pointerTracking", "clientSideProcessing"]}
            valuePropName="checked"
            extra={
              "When ON, the desktop client runs ProcessRecording (plugin post-processing) before upload. "
              + "When OFF, the raw .pointers.bin is uploaded and post-processing is handled by the backend."
            }
          >
            <Switch />
          </Form.Item>
          <Form.Item
            label="Tracker type"
            name={["pointerTracking", "options", "type"]}
            rules={[{ required: true }]}
            extra={
              "'efficient' produces a .bin file the cloud pipeline can trim; "
              + "'inefficient' produces an .ndjson file (no cloud trimming yet, but easy to read)."
            }
          >
            <Select style={{ width: 240 }}>
              {POINTER_TRACKING_TYPES.map(t => <Option key={t} value={t}>{t}</Option>)}
            </Select>
          </Form.Item>
          <Form.Item
            label="Mouse position polling rate (Hz)"
            name={["pointerTracking", "options", "mousePosRate"]}
            extra={
              "Optional — leave blank to use the plugin's default "
              + "(efficient: 1000Hz; inefficient: 125Hz). "
              + RANGE_HINT(CLAMPS.pointerTrackingMousePosRate.min, CLAMPS.pointerTrackingMousePosRate.max)
            }
          >
            <InputNumber
              min={CLAMPS.pointerTrackingMousePosRate.min}
              max={CLAMPS.pointerTrackingMousePosRate.max}
            />
          </Form.Item>
          {isEfficientTracker && (
            <>
              <Form.Item
                label="Gamepad polling rate (Hz)"
                name={["pointerTracking", "options", "gamepadRate"]}
                extra={
                  "Optional — leave blank to use the plugin default (1000Hz). "
                  + "Xbox USB controllers cap at 125Hz regardless. "
                  + RANGE_HINT(CLAMPS.pointerTrackingGamepadRate.min, CLAMPS.pointerTrackingGamepadRate.max)
                }
              >
                <InputNumber
                  min={CLAMPS.pointerTrackingGamepadRate.min}
                  max={CLAMPS.pointerTrackingGamepadRate.max}
                />
              </Form.Item>
              <Form.Item
                label="Timesync rate (Hz)"
                name={["pointerTracking", "options", "timesyncRate"]}
                extra={
                  "Optional — leave blank to use the plugin default (2Hz). "
                  + "QPC ↔ wall-clock anchors for cloud-side video alignment; 2Hz is plenty. "
                  + RANGE_HINT(CLAMPS.pointerTrackingTimesyncRate.min, CLAMPS.pointerTrackingTimesyncRate.max)
                }
              >
                <InputNumber
                  min={CLAMPS.pointerTrackingTimesyncRate.min}
                  max={CLAMPS.pointerTrackingTimesyncRate.max}
                />
              </Form.Item>
            </>
          )}
        </Panel>

        <Panel header="Encoders" key="encoders" forceRender>
          <Title level={5}>Priority (first available is used)</Title>
          <Paragraph type="secondary">
            Order matters. The desktop client uses the first encoder in this list that is valid+enabled
            on the user&apos;s machine. Duplicates and unknown names are dropped client-side.
          </Paragraph>
          <Form.List name={["encoders", "priority"]}>
            {(fields, { add, remove, move }) => (
              <>
                {fields.map((field, idx) => (
                  <Space key={field.key} style={{ display: "flex", marginBottom: 4 }} align="baseline">
                    <Form.Item {...field} noStyle rules={[{ required: true, message: "pick one" }]}>
                      <Select style={{ width: 240 }}>
                        {ENCODER_NAMES.map(n => <Option key={n} value={n}>{n}</Option>)}
                      </Select>
                    </Form.Item>
                    <Tooltip title="Move up">
                      <Button icon={<ArrowUpOutlined />} disabled={idx === 0} onClick={() => move(idx, idx - 1)} />
                    </Tooltip>
                    <Tooltip title="Move down">
                      <Button icon={<ArrowDownOutlined />} disabled={idx === fields.length - 1} onClick={() => move(idx, idx + 1)} />
                    </Tooltip>
                    <Tooltip title="Remove">
                      <Button icon={<DeleteOutlined />} danger onClick={() => remove(field.name)} />
                    </Tooltip>
                  </Space>
                ))}
                <Button type="dashed" onClick={() => add("NVIDIA_NVENC_NEW")} icon={<PlusOutlined />} style={{ marginTop: 8 }}>
                  Add encoder
                </Button>
              </>
            )}
          </Form.List>

          <Title level={5} style={{ marginTop: 24 }}>Per-encoder tuning overrides</Title>
          <Paragraph type="secondary">
            Optional. Only listed encoders override bundled defaults; unlisted encoders use their family default
            (HIGH_QUALITY/CBR for NVIDIA, QUALITY/CBR for AMD, VERYFAST/CBR for x264; INTEL ignores tuning).
          </Paragraph>
          {/*
            perEncoder is a Partial<Record<EncoderName, EncoderTuning>> in the schema — a keyed
            object, not an array. antd Form.List requires arrays, so PerEncoderEditor manages a
            local row array and writes it back to the form as a dict via form.setFieldValue.
            We register the field with a hidden Form.Item so antd is aware of it for validate/save.
          */}
          <Form.Item name={["encoders", "perEncoder"]} hidden>
            <input type="hidden" />
          </Form.Item>
          <PerEncoderEditor form={form} />
        </Panel>

        <Panel header="Session Collector" key="collector" forceRender>
          <Form.Item
            label="Heartbeat interval (ms)"
            name={["collector", "heartbeatIntervalMs"]}
            extra={RANGE_HINT(CLAMPS.heartbeatIntervalMs.min, CLAMPS.heartbeatIntervalMs.max)}
          >
            <InputNumber min={CLAMPS.heartbeatIntervalMs.min} max={CLAMPS.heartbeatIntervalMs.max} step={500} />
          </Form.Item>
          <Form.Item
            label="Mouse position sampling rate (Hz)"
            name={["collector", "mousePosHz"]}
            extra={RANGE_HINT(CLAMPS.mousePosHz.min, CLAMPS.mousePosHz.max)}
          >
            <InputNumber min={CLAMPS.mousePosHz.min} max={CLAMPS.mousePosHz.max} />
          </Form.Item>
        </Panel>

        <Panel header="Storage" key="storage" forceRender>
          <Form.Item
            label="Max video part size (MB)"
            name={["storage", "maxFilePartSizeBytes"]}
            // The wire format on the desktop is `max_file_size_bytes` (bytes) — Overwolf's
            // native unit. We display + edit in MB here for ergonomics; getValueProps and
            // normalize transform at the component boundary so the form state (and the saved
            // JSON) stays in bytes.
            getValueProps={value => ({
              value: value === null || value === undefined
                ? value
                : Math.round((value as number) / (1024 * 1024)),
            })}
            normalize={value => value === null || value === undefined ? value : (value as number) * 1024 * 1024}
            extra={
              `${RANGE_HINT(CLAMPS.maxFilePartSizeBytes.min / (1024 * 1024), CLAMPS.maxFilePartSizeBytes.max / (1024 * 1024))} MB. `
              + "1024 MB = 1 GiB. Clear the field to disable splitting (one monolithic MP4)."
            }
          >
            <InputNumber
              min={CLAMPS.maxFilePartSizeBytes.min / (1024 * 1024)}
              max={CLAMPS.maxFilePartSizeBytes.max / (1024 * 1024)}
              step={64}
            />
          </Form.Item>
          <Form.Item
            label="Recording quota (% of free disk space)"
            name={["storage", "quotaPctOfFree"]}
            extra={
              "The recording quota passed to Overwolf is computed as: floor(freeGB * this / 100). "
              + "For example, at 40% a user with 60 GB free gets a 24 GB quota; with 12 GB free they get 4 GB. "
              + RANGE_HINT(CLAMPS.quotaPctOfFree.min, CLAMPS.quotaPctOfFree.max)
            }
            rules={[{ required: true }]}
          >
            <InputNumber min={CLAMPS.quotaPctOfFree.min} max={CLAMPS.quotaPctOfFree.max} />
          </Form.Item>
          <Form.Item
            label="Min free disk space to record (GB)"
            name={["storage", "minFreeGbToRecord"]}
            extra={
              "Refuse to start recording when the drive has less than this much free space. "
              + "Below this threshold the client skips the recording entirely. "
              + RANGE_HINT(CLAMPS.minFreeGbToRecord.min, CLAMPS.minFreeGbToRecord.max)
            }
            rules={[{ required: true }]}
          >
            <InputNumber min={CLAMPS.minFreeGbToRecord.min} max={CLAMPS.minFreeGbToRecord.max} />
          </Form.Item>
        </Panel>

        <Panel header="Network Inspector" key="networkInspector" forceRender>
          <Form.Item
            label="Enabled"
            name={["networkInspector", "enabled"]}
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
          <Form.Item
            label="Enable ETW (global)"
            name={["networkInspector", "enableEtw"]}
            valuePropName="checked"
            extra="When enabled globally, ALL users get ETW collection (requires elevation).
            Use the whitelists below for targeted rollout instead."
          >
            <Switch />
          </Form.Item>
          <Form.Item
            label="ETW country whitelist"
            name={["networkInspector", "etwCountries"]}
            extra="ISO 3166-1 alpha-2 codes (e.g. IL, SK). Users in these countries get ETW collection."
          >
            <Select mode="tags" placeholder="e.g. IL, SK" tokenSeparators={[",", " "]} />
          </Form.Item>
          <Form.Item
            label="ETW account ID whitelist"
            name={["networkInspector", "etwAccountIds"]}
            extra="Specific account UUIDs to enable ETW for."
          >
            <Select mode="tags" placeholder="paste account UUIDs" tokenSeparators={[",", " "]} />
          </Form.Item>
          <Form.Item
            label="ETW email domain whitelist"
            name={["networkInspector", "etwEmailDomains"]}
            extra="Email domains (e.g. buff.game). Users with matching email get ETW collection."
          >
            <Select mode="tags" placeholder="e.g. buff.game" tokenSeparators={[",", " "]} />
          </Form.Item>
        </Panel>

        <Panel header="Upload" key="upload" forceRender>
          <Form.Item
            label="In-game upload speed cap (%)"
            name={["upload", "inGameSpeedPct"]}
            extra={
              "Upload speed cap applied while a game is running, expressed as a percentage "
              + "of the user's measured bandwidth baseline. At 20%, a 50 Mbps uplink yields a ~10 Mbps cap during gameplay. "
              + "Set to 100 to effectively disable throttling. "
              + RANGE_HINT(CLAMPS.inGameSpeedPct.min, CLAMPS.inGameSpeedPct.max)
            }
            rules={[{ required: true }]}
          >
            <InputNumber min={CLAMPS.inGameSpeedPct.min} max={CLAMPS.inGameSpeedPct.max} />
          </Form.Item>

          <Form.Item
            label="Multipart part size (MiB)"
            name={["upload", "partSizeBytes"]}
            // Wire format is bytes; we edit in MiB for ergonomics. getValueProps/normalize
            // transform at the component boundary so form state (and saved JSON) stays in bytes.
            getValueProps={value => ({
              value: value === null || value === undefined
                ? value
                : Math.round((value as number) / (1024 * 1024)),
            })}
            normalize={value => value === null || value === undefined ? value : (value as number) * 1024 * 1024}
            extra={
              "Size of each chunk in the S3 multipart upload. Smaller parts retry faster but add per-part overhead. "
              + `${RANGE_HINT(CLAMPS.partSizeBytes.min / (1024 * 1024), CLAMPS.partSizeBytes.max / (1024 * 1024))} MiB.`
            }
            rules={[{ required: true }]}
          >
            <InputNumber
              min={CLAMPS.partSizeBytes.min / (1024 * 1024)}
              max={CLAMPS.partSizeBytes.max / (1024 * 1024)}
              step={1}
              precision={0}
            />
          </Form.Item>

          <Form.Item
            label="Max part retries"
            name={["upload", "maxPartRetries"]}
            extra={
              "How many times a single multipart part is retried before the part — and the whole file — fails. "
              + RANGE_HINT(CLAMPS.maxPartRetries.min, CLAMPS.maxPartRetries.max)
            }
            rules={[{ required: true }]}
          >
            <InputNumber min={CLAMPS.maxPartRetries.min} max={CLAMPS.maxPartRetries.max} precision={0} />
          </Form.Item>

          <Form.Item
            label="Retry backoff (ms)"
            name={["upload", "retryBackoffMs"]}
            extra={
              "Initial delay before the first part retry; grows exponentially up to the cap below. "
              + RANGE_HINT(CLAMPS.retryBackoffMs.min, CLAMPS.retryBackoffMs.max)
            }
            rules={[{ required: true }]}
          >
            <InputNumber min={CLAMPS.retryBackoffMs.min} max={CLAMPS.retryBackoffMs.max} step={100} precision={0} />
          </Form.Item>

          <Form.Item
            label="Retry backoff cap (ms)"
            name={["upload", "retryBackoffCapMs"]}
            extra={
              "Upper bound on the exponential retry backoff. "
              + RANGE_HINT(CLAMPS.retryBackoffCapMs.min, CLAMPS.retryBackoffCapMs.max)
            }
            rules={[{ required: true }]}
          >
            <InputNumber min={CLAMPS.retryBackoffCapMs.min} max={CLAMPS.retryBackoffCapMs.max} step={1000} precision={0} />
          </Form.Item>

          <Form.Item
            label="S3 abort window (hours)"
            name={["upload", "s3AbortWindowMs"]}
            // Wire format is milliseconds; we edit in hours for ergonomics.
            getValueProps={value => ({
              value: value === null || value === undefined
                ? value
                : Math.round((value as number) / (60 * 60 * 1000)),
            })}
            normalize={value => value === null || value === undefined ? value : (value as number) * 60 * 60 * 1000}
            extra={
              "How long an interrupted multipart upload can still be resumed before S3 aborts it and the "
              + "recording is lost. "
              + `${RANGE_HINT(CLAMPS.s3AbortWindowMs.min / (60 * 60 * 1000), CLAMPS.s3AbortWindowMs.max / (60 * 60 * 1000))} hours `
              + "(1 h – 30 d)."
            }
            rules={[{ required: true }]}
          >
            <InputNumber
              min={CLAMPS.s3AbortWindowMs.min / (60 * 60 * 1000)}
              max={CLAMPS.s3AbortWindowMs.max / (60 * 60 * 1000)}
              step={1}
              precision={0}
            />
          </Form.Item>
        </Panel>
      </Collapse>

      <Button type="primary" onClick={onSave} style={{ marginTop: 16 }}>
        Save
      </Button>
    </Form>
  )
})

// ---------------------------------------------------------------------------
// PerEncoderEditor — separate component because the perEncoder field in the
// schema is `Partial<Record<EncoderName, EncoderTuning>>` (keyed object), not
// an array. antd `Form.List` requires arrays, so this component projects the
// object to/from an array internally and writes back via form.setFieldsValue.
// ---------------------------------------------------------------------------

type PerEncoderRow = { encoderName: EncoderName; preset: string; rateControl: string; keyframeIntervalSec: number }

function PerEncoderEditor({ form }: { form: ReturnType<typeof Form.useForm<RecordingConfigType>>[0] }) {
  // Internal local state: rows derived from form's perEncoder dict on mount.
  const [rows, setRows] = useState<PerEncoderRow[]>(() => {
    const dict = form.getFieldValue(["encoders", "perEncoder"]) ?? {}

    return Object.entries(dict).map(([encoderName, tuning]) => ({
      encoderName: encoderName as EncoderName,
      preset: (tuning as PerEncoderRow).preset,
      rateControl: (tuning as PerEncoderRow).rateControl,
      keyframeIntervalSec: (tuning as PerEncoderRow).keyframeIntervalSec,
    }))
  })

  // Whenever rows change, write back the dict into the form so save picks it up.
  useEffect(() => {
    const dict: Record<string, Omit<PerEncoderRow, "encoderName">> = {}
    rows.forEach(r => {
      dict[r.encoderName] = {
        preset: r.preset,
        rateControl: r.rateControl,
        keyframeIntervalSec: r.keyframeIntervalSec,
      }
    })
    form.setFieldValue(["encoders", "perEncoder"], dict)
  }, [rows, form])

  const updateRow = (idx: number, patch: Partial<PerEncoderRow>) => {
    setRows(prev => prev.map((r, i) => {
      if (i !== idx) return r
      const merged = { ...r, ...patch }
      if (patch.encoderName && patch.encoderName !== r.encoderName) {
        const fam = encoderFamily(patch.encoderName)
        merged.preset = presetsForFamily(fam)[0]
        merged.rateControl = rateControlsForFamily(fam)[0]
      }

      return merged
    }))
  }

  const addRow = () => {
    setRows(prev => [...prev, { encoderName: "NVIDIA_NVENC_NEW", preset: "HIGH_QUALITY", rateControl: "RC_CBR", keyframeIntervalSec: 2 }])
  }

  const removeRow = (idx: number) => {
    setRows(prev => prev.filter((_, i) => i !== idx))
  }

  return (
    <Space direction="vertical" style={{ width: "100%" }}>
      {rows.map((row, idx) => {
        const family = encoderFamily(row.encoderName)
        const presetOptions = presetsForFamily(family)
        const rcOptions = rateControlsForFamily(family)

        return (
          <Card
            key={idx}
            size="small"
            title={`Override ${idx + 1}`}
            extra={<Button icon={<DeleteOutlined />} danger size="small" onClick={() => removeRow(idx)} />}
          >
            <Space wrap>
              <div>
                <Text>Encoder</Text>
                <Select value={row.encoderName} onChange={v => updateRow(idx, { encoderName: v })} style={{ width: 220, display: "block" }}>
                  {ENCODER_NAMES.map(n => <Option key={n} value={n}>{n}</Option>)}
                </Select>
              </div>
              <div>
                <Text>Preset</Text>
                <Select value={row.preset} onChange={v => updateRow(idx, { preset: v })} style={{ width: 240, display: "block" }}>
                  {presetOptions.map(p => <Option key={p} value={p}>{p || "(none)"}</Option>)}
                </Select>
              </div>
              <div>
                <Text>Rate control</Text>
                <Select value={row.rateControl} onChange={v => updateRow(idx, { rateControl: v })} style={{ width: 180, display: "block" }}>
                  {rcOptions.map(r => <Option key={r} value={r}>{r || "(none)"}</Option>)}
                </Select>
              </div>
              <div>
                <Text>Keyframe interval (s)</Text>
                <InputNumber
                  value={row.keyframeIntervalSec}
                  onChange={v => updateRow(idx, { keyframeIntervalSec: Number(v) })}
                  min={CLAMPS.keyframeIntervalSec.min}
                  max={CLAMPS.keyframeIntervalSec.max}
                  style={{ display: "block" }}
                />
              </div>
            </Space>
          </Card>
        )
      })}
      <Button type="dashed" onClick={addRow} icon={<PlusOutlined />}>Add encoder override</Button>
    </Space>
  )
}

// Tiny deep merge — handles only plain objects, not arrays. Arrays from `incoming` replace defaults entirely.
function deepMerge<T extends Record<string, unknown>>(base: T, incoming: Partial<T>): T {
  const out: Record<string, unknown> = { ...base }
  Object.entries(incoming).forEach(([key, value]) => {
    if (value === undefined || value === null) return
    if (typeof value === "object" && !Array.isArray(value)) {
      out[key] = deepMerge((base[key as keyof T] ?? {}) as Record<string, unknown>, value as Record<string, unknown>)
    } else {
      out[key] = value
    }
  })

  return out as T
}
